import { queryOptions, useQuery } from '@tanstack/react-query';
import { ALERT_TYPES, type AlertType, getAlertComments, getComplianceAlerts, mapAlerts, mapComments } from '@/services/api/compliance-alerts';

// One stable selector per type: an inline select re-runs every render, so `data` would
// churn identity and refire every consumer memo and effect keyed on it.
type AlertSelector = (raw: Parameters<typeof mapAlerts>[0]) => ReturnType<typeof mapAlerts>;

export const ALERT_SELECTORS: Record<AlertType, AlertSelector> = Object.fromEntries(ALERT_TYPES.map((type) => [type, (raw) => mapAlerts(raw, type)])) as Record<
  AlertType,
  AlertSelector
>;

export const COMPLIANCE_ALERTS_QUERY_KEY = ['compliance', 'alerts'] as const;
// Separate list/comments prefixes so dismissals can invalidate lists without refetching comment threads
export const COMPLIANCE_ALERT_LISTS_QUERY_KEY = [...COMPLIANCE_ALERTS_QUERY_KEY, 'list'] as const;

export const complianceAlertsQueryOptions = (type: AlertType, accountHolderId?: string) =>
  queryOptions({
    queryKey: [...COMPLIANCE_ALERT_LISTS_QUERY_KEY, type, accountHolderId ?? 'all'],
    queryFn: () => getComplianceAlerts(type, accountHolderId),
    select: ALERT_SELECTORS[type],
  });

export const useComplianceAlerts = (type: AlertType, accountHolderId?: string) => useQuery(complianceAlertsQueryOptions(type, accountHolderId));

export const alertCommentsQueryOptions = (alertId: string) =>
  queryOptions({
    queryKey: [...COMPLIANCE_ALERTS_QUERY_KEY, 'comments', alertId],
    queryFn: () => getAlertComments(alertId),
    select: mapComments,
  });

export const useAlertComments = (alertId: string) => useQuery(alertCommentsQueryOptions(alertId));
